import { AlertTriangle, Inbox, LoaderCircle, RefreshCw } from 'lucide-react'
import { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { getInterfaceErrorPresentation, interfaceMessages } from '@shared/i18n/interface-messages'

type PageHeaderProps = {
	title: string
	description?: string
	actions?: ReactNode
	eyebrow?: string
}

export const PageHeader = ({ title, description, actions, eyebrow }: PageHeaderProps) => (
	<header className='flex flex-col gap-4 border-b pb-6 sm:flex-row sm:items-start sm:justify-between'>
		<div className='min-w-0'>
			{eyebrow ? (
				<p className='mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary'>
					{eyebrow}
				</p>
			) : null}
			<h1 className='text-2xl font-semibold tracking-tight sm:text-3xl'>{title}</h1>
			{description ? (
				<p className='mt-2 max-w-3xl text-sm leading-6 text-muted-foreground sm:text-base'>
					{description}
				</p>
			) : null}
		</div>
		{actions ? <div className='flex shrink-0 flex-wrap gap-2'>{actions}</div> : null}
	</header>
)

export const LoadingState = ({ label = 'Загрузка данных…' }: { label?: string }) => (
	<Card aria-live='polite' aria-busy='true'>
		<CardContent className='flex min-h-48 flex-col items-center justify-center gap-3 p-8 text-center'>
			<LoaderCircle className='h-7 w-7 animate-spin text-primary' aria-hidden='true' />
			<p className='text-sm text-muted-foreground'>{label}</p>
		</CardContent>
	</Card>
)

export const EmptyState = ({
	title,
	description,
	action,
}: {
	title: string
	description: string
	action?: ReactNode
}) => (
	<Card>
		<CardContent className='flex min-h-48 flex-col items-center justify-center gap-3 p-8 text-center'>
			<div className='rounded-full bg-muted p-3'>
				<Inbox className='h-6 w-6 text-muted-foreground' aria-hidden='true' />
			</div>
			<div>
				<h2 className='font-semibold'>{title}</h2>
				<p className='mt-1 max-w-xl text-sm leading-6 text-muted-foreground'>{description}</p>
			</div>
			{action}
		</CardContent>
	</Card>
)

export const ErrorState = ({
	error,
	onRetry,
	title,
}: {
	error: unknown
	onRetry?: () => void
	title?: string
}) => {
	const presentation = getInterfaceErrorPresentation(error, title ?? 'Не удалось загрузить данные')
	const hasDiagnostics = Boolean(presentation.code || presentation.requestId || presentation.technicalMessage)
	return (
		<Card role='alert' className='border-red-200'>
			<CardContent className='flex min-h-48 flex-col items-center justify-center gap-4 p-8 text-center'>
				<div className='rounded-full bg-red-50 p-3 text-red-600'>
					<AlertTriangle className='h-6 w-6' aria-hidden='true' />
				</div>
				<div>
					<h2 className='font-semibold'>{title ?? presentation.title}</h2>
					<p className='mt-1 max-w-xl text-sm leading-6 text-muted-foreground'>
						{presentation.description}{presentation.action ? ` ${presentation.action}` : ''}
					</p>
					{hasDiagnostics ? (
						<details className='mx-auto mt-3 max-w-xl rounded-lg border bg-background px-3 py-2 text-left text-xs text-muted-foreground'>
							<summary className='cursor-pointer font-medium'>{interfaceMessages.diagnostics.technicalDetails}</summary>
							<div className='mt-2 space-y-1 font-mono'>
								{presentation.code ? <p>{interfaceMessages.diagnostics.errorCode}: {presentation.code}</p> : null}
								{presentation.requestId ? <p>{interfaceMessages.diagnostics.requestId}: {presentation.requestId}</p> : null}
								{presentation.technicalMessage ? <p>{presentation.technicalMessage}</p> : null}
							</div>
						</details>
					) : null}
				</div>
				{onRetry ? (
					<Button variant='outline' onClick={onRetry}>
						<RefreshCw className='h-4 w-4' />
						Повторить
					</Button>
				) : null}
			</CardContent>
		</Card>
	)
}

export const InlineNotice = ({
	title,
	description,
	tone = 'info',
}: {
	title: string
	description: string
	tone?: 'info' | 'warning'
}) => (
	<div
		className={
			tone === 'warning'
				? 'rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-950'
				: 'rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-950'
		}>
		<p className='text-sm font-semibold'>{title}</p>
		<p className='mt-1 text-sm leading-6 opacity-80'>{description}</p>
	</div>
)
